import { ActionHandler } from "@/components/UI/tools";
import { Chat, Dark, Home, Settings, User } from "@/components/UI/svgs";
import { GallerySection, Placeholder, Specimen, Stage, noop } from "../galleryKit";

const NavbarDemo = () => (
    <Stage height="13rem">
        <nav className="navbar">
            <div className="navbar-row">
                <p className="navbar-brand !block">brand</p>
                <div className="flex flex-row gap-1">
                    <div className="nav-group">
                        <ActionHandler funq={noop} className="btn-ghost" title="Home"><Home /></ActionHandler>
                        <ActionHandler funq={noop} className="btn-ghost" title="Chat" selected><Chat /></ActionHandler>
                    </div>
                    <div className="nav-group">
                        <ActionHandler funq={noop} className="btn-ghost" title="Theme"><Dark /></ActionHandler>
                        <ActionHandler funq={noop} className="btn-ghost" title="Settings"><Settings /></ActionHandler>
                    </div>
                </div>
            </div>
        </nav>
        <div className="menu top-[4rem] right-[1rem] min-w-[11rem]">
            <div className="menu-item"><User size={18} /> Menu item</div>
            <div className="menu-item menu-item-accent">Highlighted item</div>
            <div className="menu-divider" />
            <div className="menu-item menu-item-disabled">Disabled item</div>
        </div>
    </Stage>
);

const PublicDemo = () => (
    <Stage height="22rem">
        <div className="public-shell !w-full">
            <div className="public-shell-veil" />
            <div className="public-shell-scroll">
                <div className="public-shell-content !min-h-full flex-row flex-wrap items-center justify-center gap-1 p-1">
                    <div className="public-card !max-w-[18rem] flex flex-col gap-1">
                        <p className="h2">Public card</p>
                        <p className="serif public-lead">Lead copy.</p>
                        <p className="serif">Marketing and legal copy sits on paper.</p>
                        <div className="public-card-image gallery-placeholder h-[4rem]">public-card-image</div>
                    </div>
                    <div className="auth-card">
                        <p className="auth-title">Auth card</p>
                        <label className="l">Email</label>
                        <input className="input" placeholder="name@example.com" />
                        <ActionHandler funq={noop} className="btn-primary btn-block">Continue</ActionHandler>
                        <div className="divider-label">or</div>
                        <ActionHandler funq={noop} className="btn-block">Other method</ActionHandler>
                    </div>
                </div>
            </div>
        </div>
    </Stage>
);

export const NavigationSection = () => (
    <GallerySection id="navigation" title="Navigation" intro="The floating top bar, grouped icon controls and dropdown menus.">
        <Specimen
            name="Navbar, nav group and menu"
            classes={["navbar", "navbar-row", "navbar-brand", "nav-group", "nav-group-vertical", "menu", "menu-item", "menu-item-accent", "menu-item-disabled", "menu-divider"]}
            usage="The navbar floats over content and lets clicks pass through except on its controls. Related icon buttons sit together in a nav-group (children use btn-ghost). Menus are positioned at the call site."
            demo={() => <NavbarDemo />}
        />
    </GallerySection>
);

export const FeedbackSection = () => (
    <GallerySection id="feedback" title="Feedback" intro="Loading, status, empty and error states.">
        <Specimen
            name="Loader and status pulse"
            classes={["loader", "status-pulse"]}
            usage="loader is the three-dot spinner (size it at the call site). status-pulse animates a dot for something in progress."
            demo={() => (
                <div className="flex flex-row items-center gap-2">
                    <div className="loader w-1 h-1" />
                    <div className="loader w-2 h-2" />
                    <svg className="status-pulse w-[0.7rem] h-[0.7rem]" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
                </div>
            )}
        />
        <Specimen
            name="Empty state"
            classes={["empty-state", "row-empty"]}
            usage="Quiet message where a list or view has nothing to show."
            demo={() => <p className="empty-state">Nothing here yet</p>}
        />
        <Specimen
            name="Error"
            classes={["error-box", "error-title", "error-text"]}
            usage="error-box holds a stack trace or raw error; error-text is an inline message under a form."
            demo={() => (
                <div className="flex flex-col gap-1 max-w-[32rem]">
                    <div>
                        <div className="error-title">Error title</div>
                        <div className="error-box">{"Error: message\n    at frame (file.ts:1:1)"}</div>
                    </div>
                    <p className="error-text">Inline error message</p>
                </div>
            )}
        />
    </GallerySection>
);

export const ContentSection = () => (
    <GallerySection id="content" title="Content" intro="Rendered documents and raw output.">
        <Specimen
            name="Rich text"
            classes={["richtext", "richtext-editing", "richtext-invalid", "richtext-toolbar", "richtext-content", "prose", "serif"]}
            usage="Shell around the MDX editor for entry body fields (rendered by EntryItem). Content is prose + serif."
            demo={() => (
                <div className="flex flex-col gap-1 max-w-[36rem]">
                    <div className="richtext"><div className="richtext-content prose dark:prose-invert serif"><p>Read-only rich text.</p></div></div>
                    <div className="richtext richtext-editing"><div className="richtext-content prose dark:prose-invert serif px-1"><p>Editing rich text.</p></div></div>
                    <div className="richtext richtext-editing richtext-invalid"><div className="richtext-content prose dark:prose-invert serif px-1"><p>Invalid rich text.</p></div></div>
                </div>
            )}
        />
        <Specimen
            name="Code block"
            classes={["code-block"]}
            usage="Preformatted output: debug dumps, stream logs, raw tool payloads."
            demo={() => <pre className="code-block">{`{\n  "key": "value"\n}`}</pre>}
        />
        <Specimen
            name="Document viewer"
            classes={["doc-viewer", "doc-viewer-fill", "doc-status", "doc-text", "pdf-artifact", "pdf-page", "pdf-byline"]}
            usage="Scroll host and typography for viewing uploaded documents (TextViewer, PdfViewer)."
            demo={() => (
                <div className="doc-viewer !min-h-0 max-w-[36rem]">
                    <p className="doc-status">Status line</p>
                    <div className="pdf-artifact px-1">
                        <h1>Document heading</h1>
                        <p className="pdf-byline">Byline</p>
                        <p>Paragraph of document text.</p>
                        <div className="pdf-page"><p>Next page.</p></div>
                    </div>
                    <p className="doc-text">Plain text document body.</p>
                </div>
            )}
        />
        <Specimen
            name="Citation and typeahead"
            classes={["citation-link", "citation-toggle", "typeahead", "typeahead-item"]}
            usage="Inline citation link and its expandable quote; typeahead lists suggestions inside the editor."
            demo={() => (
                <div className="relative h-[9rem] flex flex-col gap-1">
                    <p className="b">Text with a <span className="citation-link">citation</span> and <span className="citation-toggle">an expandable quote</span>.</p>
                    <div className="typeahead !static">
                        {["Suggestion one", "Suggestion two"].map((s) => <div key={s} className="typeahead-item">{s}</div>)}
                    </div>
                </div>
            )}
        />
    </GallerySection>
);

export const PublicSection = () => (
    <GallerySection id="public" title="Public pages" intro="Marketing, auth and legal pages on the photographic backdrop.">
        <Specimen
            name="Public shell, public card and auth card"
            classes={["public-shell", "public-shell-veil", "public-shell-scroll", "public-shell-content", "public-card", "public-lead", "public-card-image", "auth-card", "auth-title", "divider-label", "hero-backdrop"]}
            usage="Rendered by PublicPageShell; pass contentClassName for placement. Copy goes on public-card; sign-in style forms on auth-card."
            demo={() => <PublicDemo />}
        />
        <Specimen
            name="Footer"
            classes={["footer", "footer-link", "img-themed", "splash"]}
            usage="Legal footer at the bottom of public pages. img-themed inverts artwork in dark mode. splash centers a full-screen message (boot / loading)."
            demo={() => (
                <div className="flex flex-col gap-1">
                    <div className="footer !mt-0">
                        <p>Company · Address</p>
                        <p><a className="footer-link">Terms</a> · <a className="footer-link">Privacy</a></p>
                    </div>
                    <Stage height="6rem"><div className="splash"><p className="l">Splash message</p></div></Stage>
                    <Placeholder label="img-themed" />
                </div>
            )}
        />
    </GallerySection>
);
